import { InputHTMLAttributes, ReactNode } from 'react';
import styled from 'styled-components';

import { theme } from 'theme';
import { SKEW_ANGLE } from '@utils/constants';
import { ICONS } from '@utils/constants/form';
import { DefaultText } from '@components/text';

const Container = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const Label = styled(DefaultText)`
  font-size: ${({ theme }) => theme.fonts.fontSize.sm};
  color: ${({ theme }) => theme.colors.grey[200]};
`;

const InputContainer = styled.div<{ $isError?: boolean; $isSkewed?: boolean }>`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  width: 100%;
  height: 40px;
  padding: 8px 16px;
  background-color: transparent;
  border: 0.5px solid
    ${({ $isError, theme }) =>
      $isError ? theme.colors.red[500] : theme.colors.grey[300]};
  border-radius: ${({ $isSkewed }) => ($isSkewed ? '0' : '4px')};
  transform: ${({ $isSkewed }) =>
    $isSkewed ? `skew(-${SKEW_ANGLE}deg)` : 'none'};

  &:focus {
    border-color: ${({ theme }) => theme.colors.white};
  }

  &:hover {
    border-color: ${({ $isError, theme }) =>
      $isError ? theme.colors.red[500] : theme.colors.white};
  }
`;

const StyledInput = styled.input<{ $isSkewed?: boolean }>`
  flex: 1;
  background-color: transparent;
  border: none;
  outline: none;
  color: ${({ theme }) => theme.colors.white};
  font-size: ${({ theme }) => theme.fonts.fontSize.base};
  font-weight: 400;
  line-height: 1.2;
  transform: ${({ $isSkewed }) =>
    $isSkewed ? `skew(${SKEW_ANGLE}deg)` : 'none'};

  &::placeholder {
    color: ${({ theme }) => theme.colors.grey[300]};
    opacity: 0.7;
    font-size: ${({ theme }) => theme.fonts.fontSize.base};
    font-weight: 400;
    line-height: 1.2;
  }
`;

const Prefix = styled(DefaultText)<{ $isSkewed?: boolean }>`
  font-size: ${({ theme }) => theme.fonts.fontSize.sm};
  color: ${({ theme }) => theme.colors.white};
  transform: ${({ $isSkewed }) =>
    $isSkewed ? `skew(${SKEW_ANGLE}deg)` : 'none'};
  flex-shrink: 0;
`;

const IconContainer = styled.div<{ $isSkewed?: boolean }>`
  display: flex;
  align-items: center;
  transform: ${({ $isSkewed }) =>
    $isSkewed ? `skew(${SKEW_ANGLE}deg)` : 'none'};
  flex-shrink: 0;
`;

const ErrorText = styled.div`
  color: ${({ theme }) => theme.colors.red[500]};
  font-size: ${({ theme }) => theme.fonts.fontSize.xs};
  margin-top: 2px;
  float: left;
`;

const MAX_LENGTH = 250;

type Props = {
  label?: ReactNode | string;
  icon?: keyof typeof ICONS;
  errorText?: string;
  prefix?: string;
  isSkewed?: boolean;
  onChangeText?: (text: string) => void;
  customIcon?: ReactNode;
} & InputHTMLAttributes<HTMLInputElement>;

const TextInput = ({
  label,
  icon,
  errorText,
  prefix,
  isSkewed,
  onChangeText,
  customIcon,
  ...props
}: Props) => {
  const handleOnChangeText = (text: string) => {
    if (onChangeText) {
      onChangeText(text);
    }
  };

  const renderIcon = () => {
    if (icon) {
      const Icon = ICONS[icon];
      return <Icon size={16} color={theme.colors.grey[400]} />;
    }

    return null;
  };

  return (
    <Container>
      {!label ? null : typeof label === 'string' ? (
        <Label>{label}</Label>
      ) : (
        label
      )}

      <InputContainer $isError={!!errorText} $isSkewed={isSkewed}>
        {!!prefix && <Prefix $isSkewed={isSkewed}>{prefix}</Prefix>}

        <StyledInput
          maxLength={MAX_LENGTH}
          onChange={(e) => handleOnChangeText(e.target.value)}
          $isSkewed={isSkewed}
          {...props}
        />

        {customIcon ||
          (renderIcon() && (
            <IconContainer $isSkewed={isSkewed}>{renderIcon()}</IconContainer>
          ))}
      </InputContainer>

      {!!errorText && <ErrorText>{errorText}</ErrorText>}
    </Container>
  );
};

export default TextInput;
